import { QuoteLine } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  MoneyInput,
} from '@kalos/ui';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';

import { QuoteLineClientService } from '../../../tools/helpers';

interface ProposalQuickAddProps {
  serviceCallId: string;
  initialItems?: QuoteLine[];
  onContinue: (addedItems: QuoteLine[]) => void;
  onCancel: () => void;
}

interface AddedLineItem {
  tempId: string;
  description: string;
  price: number;
  quantity: number;
  flatRateId?: number; // Track original flat rate ID to prevent duplicate additions
  excludeInvoiceDiscount?: number; // 1 = exempt from percentage discount
}

export const ProposalQuickAdd: FC<ProposalQuickAddProps> = ({
  serviceCallId,
  initialItems = [],
  onContinue,
  onCancel,
}) => {
  // Convert initial QuoteLine items to AddedLineItem format
  const convertToAddedLineItems = useCallback((quoteLines: QuoteLine[]): AddedLineItem[] => {
    return quoteLines.map((line, index) => ({
      tempId: `initial-${index}-${Date.now()}`,
      description: line.description || '',
      price: parseFloat(line.adjustment || '0'),
      quantity: line.quotedQuantity || 1,
    }));
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [flatRates, setFlatRates] = useState<QuoteLine[]>([]);
  const [filteredFlatRates, setFilteredFlatRates] = useState<QuoteLine[]>([]);
  const [addedItems, setAddedItems] = useState<AddedLineItem[]>(
    convertToAddedLineItems(initialItems),
  );
  const [isLoadingFlatRates, setIsLoadingFlatRates] = useState(true);
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const [customDescription, setCustomDescription] = useState('');
  const [customPrice, setCustomPrice] = useState(0);

  // Load flat rates on mount
  useEffect(() => {
    const loadFlatRates = async () => {
      try {
        setIsLoadingFlatRates(true);

        // Load all flat rates
        const quoteLinesReq = QuoteLine.create({
          isActive: 1,
          isFlatrate: '1',
          pageNumber: 0,
          withoutLimit: true,
        });
        const res = await QuoteLineClientService.BatchGet(quoteLinesReq);
        const allFlatRates = res?.results || [];

        setFlatRates(allFlatRates);
        setFilteredFlatRates(allFlatRates);
      } catch (error) {
        console.error('Error loading flat rates:', error);
      } finally {
        setIsLoadingFlatRates(false);
      }
    };

    loadFlatRates();
  }, []);

  // Filter flat rates based on search term and exclude already added items
  useEffect(() => {
    // Get IDs of flat rates that have been added
    const addedFlatRateIds = new Set(
      addedItems.filter((item) => item.flatRateId).map((item) => item.flatRateId),
    );

    let filtered = flatRates;

    // Filter by search term if present
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = flatRates.filter(
        (rate) =>
          rate.description?.toLowerCase().includes(term) ||
          rate.adjustment?.toLowerCase().includes(term),
      );
    }

    // Exclude items that have already been added
    filtered = filtered.filter((rate) => !addedFlatRateIds.has(rate.id));

    setFilteredFlatRates(filtered);
  }, [searchTerm, flatRates, addedItems]);

  // Add flat rate item to added items list
  const handleAddFlatRate = useCallback((flatRate: QuoteLine) => {
    const newItem: AddedLineItem = {
      tempId: `flat-${Date.now()}-${Math.random()}`,
      description: flatRate.description || '',
      price: parseFloat(flatRate.adjustment || '0'),
      quantity: 1,
      flatRateId: flatRate.id, // Store the flat rate ID
      excludeInvoiceDiscount: flatRate.excludeInvoiceDiscount, // Preserve discount exemption
    };
    setAddedItems((prev) => [...prev, newItem]);
  }, []);

  // Handle custom dialog close - reset fields
  const handleCustomDialogClose = useCallback(() => {
    setCustomDescription('');
    setCustomPrice(0);
    setIsCustomDialogOpen(false);
  }, []);

  // Add custom item
  const handleAddCustomItem = useCallback(() => {
    if (!customDescription.trim() || customPrice < 0) {
      return;
    }

    const newItem: AddedLineItem = {
      tempId: `custom-${Date.now()}-${Math.random()}`,
      description: customDescription,
      price: customPrice,
      quantity: 1,
    };

    setAddedItems((prev) => [...prev, newItem]);
    handleCustomDialogClose();
  }, [customDescription, customPrice, handleCustomDialogClose]);

  // Remove item from added items
  const handleRemoveItem = useCallback((tempId: string) => {
    setAddedItems((prev) => prev.filter((item) => item.tempId !== tempId));
  }, []);

  // Calculate total
  const total = useMemo(() => {
    return addedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [addedItems]);

  // Reset all state when cancelling
  const handleCancel = useCallback(() => {
    setSearchTerm('');
    setAddedItems([]);
    setCustomDescription('');
    setCustomPrice(0);
    setIsCustomDialogOpen(false);
    onCancel();
  }, [onCancel]);

  // Handle continue button
  const handleContinue = useCallback(() => {
    // Convert added items to QuoteLine objects
    const quoteLines = addedItems.map((item) =>
      QuoteLine.create({
        description: item.description,
        adjustment: item.price.toString(),
        quotedQuantity: item.quantity,
        jobNumber: serviceCallId,
        isActive: 1,
        warranty: 2,
        excludeInvoiceDiscount: item.excludeInvoiceDiscount ?? 0, // Preserve discount exemption
      }),
    );

    onContinue(quoteLines);
  }, [addedItems, serviceCallId, onContinue]);

  return (
    <div className="space-y-4 bg-white">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">
          Proposal Quick Update - Add Line Items
        </h2>
        <p className="text-sm text-gray-600">
          Search for existing items or add custom line items to the proposal, then collect a
          signature.
        </p>
      </div>

      {/* Search and Custom Item Buttons */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white pl-10"
          />
        </div>
        <Button
          onClick={() => setIsCustomDialogOpen(true)}
          variant="outline"
          className="flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Custom Item
        </Button>
      </div>

      {/* Search Results */}
      <Card className="max-h-[300px] overflow-y-auto bg-white">
        <div className="divide-y">
          {isLoadingFlatRates ? (
            <div className="p-4 text-center text-sm text-gray-500">Loading items...</div>
          ) : filteredFlatRates.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              {searchTerm ? 'No items found matching your search' : 'No items available'}
            </div>
          ) : (
            filteredFlatRates.slice(0, 50).map((rate) => (
              <div
                key={rate.id}
                className="flex items-center justify-between bg-white p-3 hover:bg-gray-50"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {rate.description}
                    {rate.excludeInvoiceDiscount === 1 && (
                      <span className="ml-1 text-xs text-amber-600">* (discount exempt)</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    ${parseFloat(rate.adjustment || '0').toFixed(2)} × 1
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleAddFlatRate(rate)}
                  className="bg-primary hover:bg-primary/90"
                >
                  Add
                </Button>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Added Line Items */}
      {addedItems.length > 0 && (
        <div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            Added Line Items ({addedItems.length})
          </h3>
          <Card className="bg-white">
            <div className="divide-y">
              {addedItems.map((item) => (
                <div key={item.tempId} className="flex items-center justify-between bg-white p-3">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {item.description}
                      {item.excludeInvoiceDiscount === 1 && (
                        <span className="ml-1 text-xs text-amber-600">* (discount exempt)</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      ${item.price.toFixed(2)} × {item.quantity} = $
                      {(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveItem(item.tempId)}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Total */}
      {addedItems.length > 0 && (
        <div className="flex justify-end border-t pt-4">
          <div className="text-right">
            <div className="text-sm text-gray-600">Total:</div>
            <div className="text-2xl font-bold text-gray-900">${total.toFixed(2)}</div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between border-t pt-4">
        <Button variant="outline" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleContinue}
          disabled={addedItems.length === 0}
          className="bg-primary hover:bg-primary/90"
        >
          Continue to Signature
        </Button>
      </div>

      {/* Custom Item Dialog */}
      <Dialog open={isCustomDialogOpen} onOpenChange={(open) => !open && handleCustomDialogClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Line Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Description</label>
              <Input
                value={customDescription}
                onChange={(e) => setCustomDescription(e.target.value)}
                placeholder="Enter item description"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Price</label>
              <MoneyInput
                value={customPrice}
                onChange={(e) => setCustomPrice(parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCustomDialogClose}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCustomItem}
              disabled={!customDescription.trim() || customPrice < 0}
              className="bg-primary hover:bg-primary/90"
            >
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
